import { HtmlTemplateString } from "../types";

export function formatChemicalCompoundNameAsHtml(name: string): string {

    return name.replace(/(\d+)/g, (match) => "<sub>" + match + "</sub>");

}

export function formatChemicalCompoundInTextAsHtml(name: string): string {

    return name.replace(/P2O5|K2O|SO3/gi, match => formatChemicalCompoundNameAsHtml(match));

}

const subscripts = {
    "0": "\u2080",
    "1": "\u2081",
    "2": "\u2082",
    "3": "\u2083",
    "4": "\u2084",
    "5": "\u2085",
    "6": "\u2086",
    "7": "\u2087",
    "8": "\u2088",
    "9": "\u2089",
};

export function formatChemicalCompoundNameAsUnicode(name: string): string {

    return name.replace(/(\d)/g, (match) => subscripts[match]);

}

const unicodeInTextReplacements = [
    ["P2O5", "P\u2082O\u2085"],
    ["K2O", "K\u2082O"],
    ["SO3", "SO\u2083"],
].map(i => ({ regex: new RegExp(i[0], "i"), replacement: i[1] }))

export function formatChemicalCompoundInTextAsUnicode(name: string): string {

    unicodeInTextReplacements.forEach(r => {
        name = name.replace(r.regex, r.replacement)
    })

    return name

}

export function formatAsHtmlList(lines: string[]) {
    return lines
        .filter(t => !!t)
        .map(t => "<li>&ndash; " + t + "</li>")
        .join("")
}

/**
 * Parses/checks an email address
 * If "withName" is true, then addresses including a name are accepted, in the following formats:
 * Name (email address)
 * Name <email address>
 * @returns undefined: empty, false: invalid, { address: string, name: string }
 */
export function checkEmailAddress(text: string, withName: boolean) {

    if (!text) {
        return undefined
    }

    text = text.trim().replace(/[\s,;]+$/, "")

    if (!text) {
        return undefined
    }

    if (/^\S+@\S+\.\S+$/.test(text)) {
        return { address: text, name: "" }
    }

    if (!withName) {
        return false
    }

    let m = /^(.+)\s*<(\S+@\S+\.\S+)>$/.exec(text)
    if (m) {
        return { address: m[2].trim(), name: m[1] }
    }

    m = /^(.+)\s+(\S+@\S+\.\S+)$/.exec(text)
    if (m) {
        return { address: m[2].trim(), name: m[1] }
    }

    return false

}


export function formatAnyAsHtml(t: string) {

    t = t.replace(/p2o5/ig, "P<sub>2</sub>O<sub>5</sub>")
    t = t.replace(/k2o/ig, "K<sub>2</sub>O")
    t = t.replace(/sO3/ig, "SO<sub>3</sub>")
    t = t.replace(/co2/ig, "CO<sub>2</sub>")

    t = t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    t = t.replace(/__(.+?)__/g, "<em>$1</em>")

    return t

}

export function appendAreaHa(str: string, area: number) {
    return `${str} (${area}\u00a0ha)`
}


export function appendAreaM2(str: string, area: number) {
    return `${str} (${area}\u00a0m²)`
}

export function htmlLi(text: any, prefix: string = "&ndash;") {
    return `<li>${prefix}${text}</li>` as HtmlTemplateString
}

/**
 * Default `Intl.NumberFormat` for hectares.
 */
export const hectareFormat = new Intl.NumberFormat(undefined, {
    style: "unit",
    unit: "hectare",
    unitDisplay: "short",
    maximumFractionDigits: 2,
});

export const decimalFormat = new Intl.NumberFormat(undefined, {
    style: "decimal",
    maximumFractionDigits: 2,
});

/**
 * Formatter for "tonnes per hectare" values.
 * @param value The value to format
 * @returns A formatted string (e.g. "9.99 t/ha")
 */
export const tonnesPerHectareFormat = (value: number) => {
    return `${decimalFormat.format(value)} t/ha`;
}

/**
 * Formatter for "tonnes" values.
 * @param value The value to format.
 * @returns A formatted string (e.g. "9.99 t")
 */
export const tonnesFormat = (value: number) => {
    return `${decimalFormat.format(value)} t`;
}

/**
 * Formatter returning number string with commas, e.g. 12,234.01
 * @param value The value to format.
 * @param fractionSize Size of the fraction part.
 * @param fallback Returned when the value is not a valid number.
 * @returns 
 */
export const formatToReadable = (
    value: number | string,
    fractionSize: number = 3,
    fallback: string = ''
): string => {
    const num = Number(value);
    if (isNaN(num)) return fallback;

    return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: fractionSize });
}